import type { Result, AppError } from '../../error/types';
import type { EnvironmentValue } from '../types/EnvironmentValue';
import { ok, err } from '../../error/functions';

/**
 * @group Functions
 * @summary <blockquote>Reusable utility for getting environment variable values with proper error handling using the Result pattern.</blockquote>
 * 
 * @remarks
 * This function provides a centralized way to access environment variables with consistent error handling.
 * It returns a Result type that can be safely handled throughout the application.
 * 
 * @param envKey - The environment variable key to retrieve
 * @param required - Whether the environment variable is required (defaults to false)
 * @returns Result containing the environment variable value or an AppError if not found/required
 * 
 * @example
 * ```typescript
 * import { getEnvironmentValue } from '@shared/utils';
 * 
 * // Get optional environment variable
 * const result = getEnvironmentValue('DATABASE_URL');
 * result.match({
 *   ok: (value) => {
 *     console.log(`Database URL: ${value}`);
 *   },
 *   err: (error) => {
 *     console.log('No database URL set');
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Get required environment variable
 * const result = getEnvironmentValue('AUTH_SECRET', true);
 * result.match({
 *   ok: (value) => {
 *     // Use the secret
 *   },
 *   err: (error) => {
 *     console.error(`Required env var missing: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Using with ENVIRONMENT_VALUES constants
 * import { getEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
 * 
 * const dbUrlResult = getEnvironmentValue(ENVIRONMENT_VALUES.DATABASE.POSTGRES_URL);
 * const authSecretResult = getEnvironmentValue(ENVIRONMENT_VALUES.AUTH.AUTH_SECRET, true);
 * ```
 */
export function getEnvironmentValue(
  envKey: EnvironmentValue, 
  required: boolean = false
): Result<string, AppError> {
  const value = process.env[envKey];
  
  if (!value) {
    if (required) {
      return err({
        errorCode: 'MISSING_REQUIRED_ENV_VAR',
        errorMessage: `Required environment variable '${envKey}' is not set`
      });
    }
    
    return err({
      errorCode: 'MISSING_ENV_VAR',
      errorMessage: `Environment variable '${envKey}' is not set`
    });
  }
  
  return ok(value);
} 